export type ThemeMode = 'light' | 'dark'

export const TOKEN_KEY = 'pp_token'
export const THEME_KEY = 'pp-theme'

export const MT5_DOWNLOAD_URL =
  'https://download.mql5.com/cdn/web/metaquotes.software.corp/mt5/mt5setup.exe'

/** Only these MT5 symbols may appear as Perfect Predict Portal predictions. */
export const APPROVED_PREDICTION_SYMBOLS = [
  'PREDGOLD_SAFE',
  'PREDGOLD_BAL',
  'PREDGOLD_AGG',
] as const

export type ApprovedPredictionSymbol = (typeof APPROVED_PREDICTION_SYMBOLS)[number]

export type QuoteState =
  | 'LIVE'
  | 'STALE'
  | 'UNAVAILABLE'
  | 'FIXTURE_REVIEW_ONLY'
  | 'MARKET_CLOSED'

export type SymbolQuote = {
  symbol: string
  bid: number | null
  ask: number | null
  last: number | null
  spread: number | null
  quoteTimestamp: string | null
  tradeEnabled: boolean
  marketStatus: 'OPEN' | 'CLOSED' | 'UNKNOWN'
  syncState: QuoteState
}

export type PredictionStrategy = {
  id: 'safe' | 'balanced' | 'aggressive'
  displayName: string
  description: string
  /** Predefined MT5 symbol — Portal never trades XAUUSD directly. */
  mt5Symbol: ApprovedPredictionSymbol
  targetMovement: number
  stopMovement: number
  profitPercent: number
  totalReturnPercent: number
  availability: 'AVAILABLE' | 'COMING_SOON' | 'DISABLED'
  /** Default quote readiness until Symbol Feed API is live. */
  quoteState: QuoteState
  productFamily: 'GOLD'
}

export type TradingAccount = {
  id: number
  mt5Login: number
  server: string
  mode: string
  currency: string
  balance: number
  equity: number
  status: string
  createdAt: string
  updatedAt: string
  sync_state?: string
  last_sync_at?: string
  floating_profit?: number
}

export type AccountSummary = {
  id: number
  mt5_login: number
  server: string
  mode: string
  currency: string
  balance: number
  equity: number
  floating_profit: number
  margin?: number
  free_margin?: number
  margin_level?: number
  /** Present only if backend ever exposes it — Portal never invents leverage. */
  leverage?: number
  status: string
  sync_state: string
  last_sync_at: string
}

export type Position = {
  ticket: number
  symbol: string
  side: string
  volume: number
  open_price: number
  current_price: number
  floating_profit: number
}

export type TradeHistoryItem = {
  ticket?: number
  position_id?: number
  symbol?: string
  side?: string
  volume?: number
  open_price?: number
  close_price?: number
  profit?: number
  net_profit?: number
  commission?: number
  swap?: number
  fee?: number
  time?: string
  open_time?: string
  close_time?: string
  activity_type?: string | null
  /** WON | LOST | BREAKEVEN when provided by API */
  result?: string
}

export type AccountActivityItem = {
  deal_or_ticket_id?: number
  activity_type?: string
  amount?: number
  currency?: string
  timestamp?: string
}

export type FinanceTx = {
  id?: number
  reference?: string
  type?: string
  trading_account_id?: number
  mt5_login_masked?: string
  amount?: string | number
  currency?: string
  status?: string
  method?: string
  provider?: string
  date?: string
  created_at?: string
  completed_at?: string
  is_demo?: boolean
  rejection_reason?: string
}

/**
 * Client-facing strategies mapped to predefined MT5 symbols.
 * Portal is a simplified UI over these symbols only — not an external price feed.
 */
export const PREDICTION_STRATEGIES: readonly PredictionStrategy[] = [
  {
    id: 'safe',
    displayName: 'Safe Prediction',
    description: 'Lower relative exposure with matched target and stop distance.',
    mt5Symbol: 'PREDGOLD_SAFE',
    targetMovement: 5,
    stopMovement: 5,
    profitPercent: 60,
    totalReturnPercent: 160,
    availability: 'AVAILABLE',
    quoteState: 'UNAVAILABLE',
    productFamily: 'GOLD',
  },
  {
    id: 'balanced',
    displayName: 'Balanced Prediction',
    description: 'Measured conviction for typical market conditions.',
    mt5Symbol: 'PREDGOLD_BAL',
    targetMovement: 10,
    stopMovement: 5,
    profitPercent: 150,
    totalReturnPercent: 250,
    availability: 'AVAILABLE',
    quoteState: 'UNAVAILABLE',
    productFamily: 'GOLD',
  },
  {
    id: 'aggressive',
    displayName: 'Aggressive Prediction',
    description: 'Higher conviction when opportunity is clear.',
    mt5Symbol: 'PREDGOLD_AGG',
    targetMovement: 20,
    stopMovement: 5,
    profitPercent: 300,
    totalReturnPercent: 400,
    availability: 'AVAILABLE',
    quoteState: 'UNAVAILABLE',
    productFamily: 'GOLD',
  },
] as const

/** Product families offered in Portal (not raw MT5 majors like XAUUSD). */
export const PRODUCT_FAMILIES = [
  {
    id: 'GOLD',
    label: 'Gold',
    description: 'Maps to predefined Perfect Predict MT5 Gold symbols only.',
    available: true,
  },
  {
    id: 'SILVER',
    label: 'Silver',
    description: 'Coming later — requires MT5 symbols, quotes, trading enablement, and approved rules.',
    available: false,
  },
] as const

/** @deprecated Use PREDICTION_STRATEGIES */
export const PREDICTION_RULES = PREDICTION_STRATEGIES

export const sidebarNav = [
  { to: '/app', label: 'Dashboard', end: true },
  { to: '/app/accounts', label: 'Trading Accounts' },
  { to: '/app/trading-history', label: 'Trading History' },
  { to: '/app/trade', label: 'Trade Now' },
  { to: '/app/deposits', label: 'Deposits' },
  { to: '/app/withdrawals', label: 'Withdrawals' },
  { to: '/app/transactions', label: 'Transactions' },
  { to: '/app/support', label: 'Support' },
  { to: '/app/profile', label: 'Profile' },
] as const

export const moreNav = [
  { to: '/app/trade', label: 'Trade Now' },
  { to: '/app/deposits', label: 'Deposits' },
  { to: '/app/withdrawals', label: 'Withdrawals' },
  { to: '/app/profile', label: 'Profile' },
  { to: '/app/support', label: 'Support' },
] as const
